import { Pin, X } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

import {
  ACTIVITY_PANEL_EVENTS,
  ACTIVITY_PANEL_PROPERTIES,
} from '@/constants/analytics/activity-panel-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';
import { enhancedApi } from '@/services/api/generated/matterEventsService';

import { EventFeedSkeleton } from './EventFeedSkeleton';
import { EventList } from './EventList';
import { PinnedEventsProvider } from './PinnedEventsContext';
import { DomainEvent, FinchEventCode } from './types';
import { usePinnedEvents } from './usePinnedEvents';

// Activity events to show (excluding staff notes which go to Notes panel)
const ACTIVITY_EVENTS: FinchEventCode[] = [
  'matter_created',
  'matter_updated',
  'matter_status_changed',
  'task_created',
  'task_status_changed',
  'task_assigned',
  'task_deleted',
  'treatment_check_in_note_logged',
];

// Filter options for activity types
const ACTIVITY_FILTER_OPTIONS: {
  key: string;
  label: string;
  eventCodes: FinchEventCode[];
}[] = [
  {
    key: 'MATTER',
    label: 'Matter Updates',
    eventCodes: ['matter_created', 'matter_updated', 'matter_status_changed'],
  },
  {
    key: 'TASK',
    label: 'Task Updates',
    eventCodes: [
      'task_created',
      'task_status_changed',
      'task_assigned',
      'task_deleted',
    ],
  },
  {
    key: 'TREATMENT',
    label: 'Treatment',
    eventCodes: ['treatment_check_in_note_logged'],
  },
];

interface DomainEventsFeedProps {
  matterId: string;
  eventCode?: FinchEventCode;
  eventCodes?: FinchEventCode[];
  limit?: number;
  targetId?: string;
}

// Filter pills component extracted to module level
interface FilterPillsProps {
  selectedFilterKeys: string[];
  onToggleFilter: (key: string) => void;
  onClearFilters: () => void;
}

const FilterPills: React.FC<FilterPillsProps> = ({
  selectedFilterKeys,
  onToggleFilter,
  onClearFilters,
}) => (
  <div className="bg-white px-3 py-2 border-b border-zinc-100">
    <div className="flex flex-wrap items-center gap-1.5">
      {ACTIVITY_FILTER_OPTIONS.map((opt) => {
        const isSelected = selectedFilterKeys.includes(opt.key);
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onToggleFilter(opt.key)}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors',
              isSelected
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200',
            )}
          >
            {opt.label}
            {isSelected && <X className="h-2.5 w-2.5" />}
          </button>
        );
      })}
      {selectedFilterKeys.length > 0 && (
        <button
          type="button"
          onClick={onClearFilters}
          className="text-[10px] text-zinc-500 hover:text-zinc-700 ml-1"
        >
          Clear all
        </button>
      )}
    </div>
  </div>
);

export const DomainEventsFeed: React.FC<DomainEventsFeedProps> = ({
  matterId,
  eventCode,
  eventCodes,
  limit = 50,
  targetId,
}) => {
  const capture = usePostHogCapture();

  // Filter state
  const [selectedFilterKeys, setSelectedFilterKeys] = useState<string[]>([]);

  // Pinned events
  const { pinnedEventIds, isPinned, togglePin } = usePinnedEvents(matterId);

  const toggleFilter = useCallback(
    (key: string) => {
      setSelectedFilterKeys((prev) => {
        const isRemoving = prev.includes(key);
        const next = isRemoving
          ? prev.filter((k) => k !== key)
          : [...prev, key];

        // Track filter toggle event
        capture(ACTIVITY_PANEL_EVENTS.ACTIVITY_FILTER_TOGGLED, {
          [ACTIVITY_PANEL_PROPERTIES.PAGE]: 'matters',
          [ACTIVITY_PANEL_PROPERTIES.COMPONENT]: 'activity_panel',
          [ACTIVITY_PANEL_PROPERTIES.MATTER_ID]: matterId,
          [ACTIVITY_PANEL_PROPERTIES.FILTER_KEY]: key,
          [ACTIVITY_PANEL_PROPERTIES.FILTER_ACTION]: isRemoving
            ? 'removed'
            : 'added',
          [ACTIVITY_PANEL_PROPERTIES.ACTIVE_FILTERS]: next.join(','),
          [ACTIVITY_PANEL_PROPERTIES.ACTIVE_FILTER_COUNT]: next.length,
        });

        return next;
      });
    },
    [capture, matterId],
  );

  const clearFilters = useCallback(() => {
    capture(ACTIVITY_PANEL_EVENTS.ACTIVITY_FILTERS_CLEARED, {
      [ACTIVITY_PANEL_PROPERTIES.PAGE]: 'matters',
      [ACTIVITY_PANEL_PROPERTIES.COMPONENT]: 'activity_panel',
      [ACTIVITY_PANEL_PROPERTIES.MATTER_ID]: matterId,
    });
    setSelectedFilterKeys([]);
  }, [capture, matterId]);

  const {
    data: eventsResponse,
    isLoading,
    error,
  } = enhancedApi.useMatterDomainEventsListQuery(
    {
      matterId,
      eventCode: eventCode ? [eventCode] : undefined,
      eventCodeIn: eventCodes
        ? eventCodes.join(',')
        : ACTIVITY_EVENTS.join(','),
      limit,
      targetId,
      ordering: '-occurred_at',
    },
    {
      pollingInterval: 5000,
      skipPollingIfUnfocused: true,
    },
  );

  // Get filtered events and separate pinned for the top section
  const { pinnedEvents, allFilteredEvents } = useMemo(() => {
    const events = eventsResponse?.results || [];

    // Apply filters first
    let filteredEvents = events;
    if (selectedFilterKeys.length > 0) {
      const selectedEventCodes = new Set<FinchEventCode>();
      selectedFilterKeys.forEach((key) => {
        const option = ACTIVITY_FILTER_OPTIONS.find((opt) => opt.key === key);
        if (option) {
          option.eventCodes.forEach((code) => selectedEventCodes.add(code));
        }
      });
      filteredEvents = events.filter((event: DomainEvent) =>
        selectedEventCodes.has(event.event_code),
      );
    }

    // Get pinned events for the top section
    const pinned = filteredEvents.filter((event: DomainEvent) =>
      pinnedEventIds.has(event.id),
    );

    return { pinnedEvents: pinned, allFilteredEvents: filteredEvents };
  }, [eventsResponse?.results, selectedFilterKeys, pinnedEventIds]);

  // Loading state
  if (isLoading) {
    return <EventFeedSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <div className="text-center">
          <p className="text-xs text-zinc-600">Failed to load activity</p>
          <p className="text-[10px] text-zinc-400 mt-0.5">
            Please try again later
          </p>
        </div>
      </div>
    );
  }

  const isFiltered = selectedFilterKeys.length > 0;
  const hasNoEvents = allFilteredEvents.length === 0;

  // Empty state
  if (hasNoEvents) {
    return (
      <div className="flex flex-col h-full">
        <FilterPills
          selectedFilterKeys={selectedFilterKeys}
          onToggleFilter={toggleFilter}
          onClearFilters={clearFilters}
        />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <p className="text-xs text-zinc-500">
              {isFiltered ? 'No matching activity' : 'No activity yet'}
            </p>
            <p className="text-[10px] text-zinc-400 mt-0.5">
              {isFiltered
                ? 'Try adjusting your filters'
                : 'Activity will appear here'}
            </p>
            {isFiltered && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-[10px] text-blue-600 hover:text-blue-700 mt-2"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <PinnedEventsProvider value={{ isPinned, togglePin }}>
      <div className="flex flex-col h-full">
        {/* Filter Pills */}
        <FilterPills
          selectedFilterKeys={selectedFilterKeys}
          onToggleFilter={toggleFilter}
          onClearFilters={clearFilters}
        />

        {/* Events list */}
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {/* Pinned Section */}
          {pinnedEvents.length > 0 && (
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <div className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 rounded">
                    <Pin className="h-2.5 w-2.5 text-blue-600" />
                    <span className="text-[10px] font-medium text-blue-700 uppercase tracking-wide">
                      Pinned
                    </span>
                  </div>
                  <span className="text-[10px] text-zinc-400">
                    ({pinnedEvents.length})
                  </span>
                </div>
                <span className="text-[9px] text-zinc-800 font-medium italic">
                  Only visible to you
                </span>
              </div>
              <EventList events={pinnedEvents} />
              <div className="flex items-center gap-2 mt-3">
                <div className="flex-1 h-px bg-zinc-200" />
                <span className="text-[10px] text-zinc-400">All Activity</span>
                <div className="flex-1 h-px bg-zinc-200" />
              </div>
            </div>
          )}
          {/* All Events (including pinned with indicator) */}
          <EventList events={allFilteredEvents} />
        </div>
      </div>
    </PinnedEventsProvider>
  );
};
